import Component from '@glimmer/component';
import { action } from '@ember/object';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import luxonPlugin from '@fullcalendar/luxon3';
import interactionPlugin from '@fullcalendar/interaction';
import { task, timeout, didCancel } from 'ember-concurrency';
import { makeArray } from '@ember/array';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import _ from 'lodash';

export default class DeluxePlayerScheduleComponent extends Component {
  @service clock;
  @service intl;
  @service router;
  @service socket;
  @service store;

  @tracked sizing = {};
  @tracked dimensions = {};
  @tracked viewTitle;
  @tracked dateSelectMode;

  // ------------------------ INITIALIZATION -------------------------------//

  // colorize = new Colors(this.args.station, this.store);

  // ------------------------ EVENT SOURCES -------------------------------//

  eventSources = [
    {
      id: 'airings',
      events: async (fetchInfo, success, failure) => {
        try {
          let events = await this.fetchAiringsInRangeTask.perform(
            fetchInfo.start,
            fetchInfo.end,
          );
          success(events);
        } catch (e) {
          if (!didCancel(e)) {
            throw e;
          }
        }
      },
    },
  ];

  get startDate() {
    return this.args.startDate === 'today' ? new Date() : this.args.startDate;
  }

  // ------------------------ EVENT FETCHING -------------------------------//

  fetchAiringsInRangeTask = task(
    { restartable: true, maxConcurrency: 1 },
    async (startDate, endDate, options = {}) => {
      await timeout(200);
      let extraIncludes = makeArray(options.extraIncludes) || [];

      let airings = await this.store.query('airing', {
        include: ['broadcast', 'channels', 'show', ...extraIncludes].join(','),
        stats: { total: 'count' },
        filter: {
          starts_at: {
            lte: endDate.toISOString(),
          },
          ends_at: {
            gte: startDate.toISOString(),
          },
          station_id: this.args.station.id,
        },
        stationUid: this.args.station.uid,
        // extra_fields: { shows: ['image'] },
        page: { size: 1000 }, // we want all
      });

      let events = airings.map((airing) => this.airingToEvent(airing));
      return events;
    }
  );

  // ------------------------ EVENT HELPERS -------------------------------//

  airingToEvent(airing) {
    return {
      id: airing.id,
      title: airing.showTitle,
      url: 'https://koop.org',
      // url: this.router.urlFor(
      //   'station.show-detail.airing',
      //   airing.show.slug,
      //   airing.id,
      // ),
      start: airing.startsAt,
      end: airing.endsAt,
      display: airing.isOverridden ? 'none' : 'list-item',
      extendedProps: {
        airing,
        show: airing.show,
        showId: airing.belongsTo('show').id(),
        subtitle: airing.title,
      },
      // ...this.eventColorValuesFromAiring(airing),
    };
  }

  isMissingPlayback(event) {
    let airing = event.extendedProps?.airing;
    return (
      airing &&
      this.profile.features.playback &&
      !airing.isOverridden &&
      !airing.playbackUrl &&
      !this.args.isEditing
    );
  }

  @action
  eventClassNames(args) {
    let klasses = [];
    // let airing = args.event?.extendedProps?.airing;
    // if (airing) {
    //   if (args.isFuture) {
    //     klasses.push('is-future');
    //   }
    //   if (args.isPast) {
    //     klasses.push('is-past');

    //     if (this.isMissingPlayback(args.event)) {
    //       klasses.push('is-missing-playback');
    //     }
    //   }
    //   if (airing.isOverridden) {
    //     klasses.push('is-overridden');
    //   }
    //   if (airing.isOverriding) {
    //     klasses.push('is-overriding');
    //   }
    //   if (airing?.scheduleType === 'special') {
    //     klasses.push('is-special');
    //   }
    //   if (airing.scheduleSeriesPosition === 'first') {
    //     klasses.push('is-first')
    //   }
    //   if (airing.scheduleSeriesPosition === 'last') {
    //     klasses.push('is-last')
    //   }
    // }

    // if (args.event.extendedProps.isDirty || args.event.extendedProps.isNew) {
    //   klasses.push('is-dirty');
    // }
    // if (args.event.extendedProps.isSelected) {
    //   klasses.push('is-selected');
    // }
    // if (args.event.extendedProps.isRemoved) {
    //   klasses.push('is-removed');
    // }
    // if (args.event.extendedProps.isCascading) {
    //   klasses.push('is-cascading');
    // }
    // if (args.event.extendedProps.isErrored) {
    //   klasses.push('is-errored');
    //   klasses.push('u-is-shaking')
    // }

    // if (differenceInMinutes(args.event.end, args.event.start) <= 25) {
    //   klasses.push('is-short');
    // }

    // if (differenceInMinutes(args.event.end, args.event.start) <= 15) {
    //   klasses.push('is-tiny');
    // }

    return klasses.join(' ');
  }

  // ------------------------ EVENT HANDLERS -------------------------------//

  @action
  onTimeZoneChange(value) {
    this.calendar.setOption('timeZone', value);
    this.calendar.render();
  }

  @action
  onLocaleChange(value) {
    this.calendar.setOption('locale', value);
    this.calendar.render();
  }

  @action
  async refreshAll() {
    if (!this.args.isEditing) {
      this.calendar.refetchEvents();
    }
  }

  @action
  async setDate(startDate) {
    this.calendar.gotoDate(startDate == 'today' ? new Date() : startDate); // Accepts ISO date strings
  }

  @action
  initializeCalendar(element) {
    this.element = element;
    this.calendar = new Calendar(this.element, {
      aspectRatio: 0,
      allDaySlot: false,
      editable: false,
      selectable: false,
      eventClassNames: this.eventClassNames,
      eventSources: this.eventSources,
      eventTimeFormat: {
        hour: 'numeric',
        minute: '2-digit',
        omitZeroMinute: true,
        meridiem: 'short',
      },
      headerToolbar: false,
      fixedWeekCount: false,
      initialDate: this.startDate,
      initialView: 'listMonth',
      // locale: this.intl.primaryLocale,
      navLinks: false,
      nowIndicator: true,
      nowIndicatorClassNames: ['now-indicator'],
      plugins: [
        luxonPlugin,
        dayGridPlugin,
        timeGridPlugin,
        listPlugin,
        interactionPlugin,
      ],
      progressiveEventRendering: true,
      slotDuration: '00:10:00',
      snapDuration: '00:05:00',
      selectMinDistance: 10,
      selectLongPressDelay: 0,
      slotLabelInterval: '01:00:00',
      height: 'auto',
      stickyHeaderDates: false,
      contentHeight: 'auto',
      timeZone: this.clock.timeZone,
      // viewClassNames: this.viewClassNames,
      // views: {
      //   listWeek: {
      //     type: 'listWeek',
      //     buttonText: 'List',
      //   },
      // },
      datesSet: this.onPageChange,
      windowResizeDelay: 50,
    });

    this.calendar.render();
    this.isLoaded = true;
    window.calendar = this.calendar;
  }

  // ------------------------ CELL EDIT MENUS -------------------------------//

  // ------------------------ COMPONENT EVENT HOOKS -------------------------------//

  @tracked element;
  @tracked isLoaded;

  @action
  didInsert(element) {
    this.element = element;
    this.initializeCalendar(element);
  }
}
